import type { Session } from '@supabase/supabase-js';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { isStudentEmail } from '../lib/accounts';
import { fetchBilling, hasAccess, type Billing } from '../lib/billing';
import { fetchMyCards, type Card } from '../lib/cards';
import {
  fetchAssignments, fetchClassrooms, fetchExtensions, type Extension, fetchMembers, fetchMyMembership, fetchMyStudentAccount, fetchPasses, fetchProgress,
} from '../lib/api';
import { today } from '../lib/dates';
import { byDue, isOut } from '../lib/kinds';
import { supabase } from '../lib/supabase';
import type {
  Assignment, AssignmentKind, Classroom, Member, Pass, Progress, Role, StudentAccount,
} from '../lib/types';

// ─────────────────────────────────────────────────────────────────────────────
// One context for the whole site: who is signed in, which class is open, and
// that class's roster, assignments and progress.
//
// ROLE comes from the database, not the address. A row in student_accounts is
// what a student is; the synthetic email is only a hint used to pick the right
// screen while that row loads.
// ─────────────────────────────────────────────────────────────────────────────

type Ctx = {
  session: Session | null;
  authReady: boolean;
  role: Role | null;
  student: StudentAccount | null;

  hydrated: boolean;
  error: string | null;

  classrooms: Classroom[];
  active: Classroom | null;
  setActiveId: (id: string) => void;

  /** Teacher only. */
  members: Member[];
  students: StudentAccount[];

  /** Everything assigned or scheduled (drafts are kept apart, in `drafts`). */
  assignments: Assignment[];
  /** Teacher only: saved for later, not assigned. */
  drafts: Assignment[];
  /** Keyed `${assignment_id}:${student_id}`. */
  progress: Map<string, Progress>;
  progressFor: (assignmentId: string, studentId?: string) => Progress | undefined;
  /** Finished passes, in order. */
  passesFor: (assignmentId: string, studentId?: string) => Pass[];
  /** Re-read one assignment's passes after a pass is finished. */
  reloadPasses: (assignmentId: string) => Promise<void>;

  /** The newest assignment of a kind whose week has started. */
  current: (kind: AssignmentKind) => Assignment | undefined;

  refresh: () => Promise<void>;
  /** Due-date extensions: a student's own (students), or the whole class's (teachers). */
  extensions: Extension[];
  /** A student's due date for an assignment: their extension, or the assignment's own. */
  dueFor: (a: Assignment, studentId: string) => string | null;
  reloadExtensions: () => Promise<void>;
  upsertProgress: (p: Progress) => void;

  /** Teacher only: their subscription row (undefined while loading). */
  billing: Billing | null | undefined;
  /** Teacher only: may make classes and assignments (trial, subscribed or comped). */
  canCreate: boolean;
  reloadBilling: () => Promise<void>;

  /** Student only: their flashcard deck. */
  cards: Card[];
  setCards: React.Dispatch<React.SetStateAction<Card[]>>;
  reloadCards: () => Promise<void>;
};

/** Exported for the dev-only demo pages (snake-demo.html), which give it made-up data. */
export const ClassroomContext = createContext<Ctx | null>(null);

const ACTIVE_KEY = 'classroom.active';

function readActive(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
function writeActive(id: string) {
  try { localStorage.setItem(ACTIVE_KEY, id); } catch { /* private mode */ }
}

export function ClassroomProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [student, setStudent] = useState<StudentAccount | null>(null);
  const [roleLoaded, setRoleLoaded] = useState(false);

  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(readActive);
  const [members, setMembers] = useState<Member[]>([]);
  const [students, setStudents] = useState<StudentAccount[]>([]);
  const [allAssignments, setAssignments] = useState<Assignment[]>([]);
  const assignments = useMemo(() => allAssignments.filter(a => !a.draft), [allAssignments]);
  const drafts = useMemo(() => allAssignments.filter(a => a.draft), [allAssignments]);
  const [progress, setProgress] = useState<Map<string, Progress>>(new Map());
  const [passes, setPasses] = useState<Pass[]>([]);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [billing, setBilling] = useState<Billing | null | undefined>(undefined);
  const [billingFailed, setBillingFailed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;

  // Role.
  useEffect(() => {
    setRoleLoaded(false);
    setStudent(null);
    if (!userId) return;
    let cancelled = false;
    fetchMyStudentAccount(userId)
      .then(s => { if (!cancelled) { setStudent(s); setRoleLoaded(true); } })
      .catch(e => { if (!cancelled) { setError(String(e.message ?? e)); setRoleLoaded(true); } });
    return () => { cancelled = true; };
  }, [userId]);

  const role: Role | null = !session
    ? null
    : roleLoaded
      ? (student ? 'student' : 'teacher')
      : (isStudentEmail(session.user.email) ? 'student' : null);

  const active = useMemo(
    () => classrooms.find(c => c.id === activeId) ?? classrooms[0] ?? null,
    [classrooms, activeId],
  );

  const setActiveId = useCallback((id: string) => {
    setActiveIdState(id);
    writeActive(id);
  }, []);

  const load = useCallback(async () => {
    if (!userId || !roleLoaded) return;
    setError(null);
    try {
      const cs = await fetchClassrooms();
      setClassrooms(cs);
      const cur = cs.find(c => c.id === activeId) ?? cs[0] ?? null;
      if (!cur) {
        setMembers([]); setStudents([]); setAssignments([]); setProgress(new Map()); setPasses([]);
        return;
      }
      const isTeacher = !student;
      const [as, roster] = await Promise.all([
        fetchAssignments(cur.id),
        // A student gets only their own row: it carries their reading-time share.
        isTeacher ? fetchMembers(cur.id)
          : fetchMyMembership(cur.id, userId).then(m => ({ members: m ? [m] : [], students: [] })),
      ]);
      const ids = as.map(a => a.id);
      const [ps, pass, ext] = await Promise.all([fetchProgress(ids), fetchPasses(ids), fetchExtensions(ids)]);
      setPasses(pass);
      setExtensions(ext);
      setAssignments(as);
      setMembers(roster.members);
      setStudents(roster.students);
      setProgress(new Map(ps.map(p => [`${p.assignment_id}:${p.student_id}`, p])));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setHydrated(true);
    }
  }, [userId, roleLoaded, student, activeId]);

  useEffect(() => {
    if (!userId) {
      setHydrated(false);
      setClassrooms([]); setAssignments([]); setMembers([]); setStudents([]); setProgress(new Map()); setPasses([]);
      return;
    }
    void load();
  }, [userId, load]);

  // The deck belongs to the student, not to a class: one deck across classes.
  const reloadCards = useCallback(async () => {
    try { setCards(await fetchMyCards()); } catch (e) { console.error('[cards]', e); }
  }, []);
  useEffect(() => {
    if (!userId || !student) { setCards([]); return; }
    void reloadCards();
  }, [userId, student, reloadCards]);

  // A teacher's subscription. Opening the site the first time starts the trial.
  const reloadBilling = useCallback(async () => {
    try { setBilling(await fetchBilling()); setBillingFailed(false); }
    catch (e) { console.error('[billing]', e); setBillingFailed(true); }
  }, []);
  useEffect(() => {
    setBilling(undefined);
    if (!userId || !roleLoaded || student) return;
    void reloadBilling();
  }, [userId, roleLoaded, student, reloadBilling]);
  // Unknown (still loading, or the lookup failed) never locks the page: the
  // database refuses for real, so the page only has to explain it.
  const canCreate = billing === undefined || billingFailed || hasAccess(billing);

  const upsertProgress = useCallback((p: Progress) => {
    setProgress(prev => {
      const next = new Map(prev);
      next.set(`${p.assignment_id}:${p.student_id}`, p);
      return next;
    });
  }, []);

  const progressFor = useCallback(
    (assignmentId: string, studentId?: string) =>
      progress.get(`${assignmentId}:${studentId ?? userId}`),
    [progress, userId],
  );

  const passesFor = useCallback(
    (assignmentId: string, studentId?: string) => passes.filter(
      p => p.assignment_id === assignmentId && p.student_id === (studentId ?? userId),
    ),
    [passes, userId],
  );

  const reloadPasses = useCallback(async (assignmentId: string) => {
    try {
      const fresh = await fetchPasses([assignmentId]);
      setPasses(prev => [...prev.filter(p => p.assignment_id !== assignmentId), ...fresh]);
    } catch { /* the next full refresh picks them up */ }
  }, []);

  // Past-due assignments vanish for students (the database hides them too, 20261035); this also
  // covers a page left open across the due date.
  // A student with an extension sees their own due date instead of the class's.
  const t0 = today();
  const visible = useMemo(() => {
    if (!student) return assignments;
    const mine = new Map(extensions.map(e => [e.assignment_id, e.due_date]));
    return assignments.map(a => (mine.has(a.id) ? { ...a, due_date: mine.get(a.id)! } : a))
      .filter(a => !a.due_date || a.due_date >= t0);
  }, [assignments, student, extensions, t0]);
  /** A student's due date for an assignment: their extension, or the assignment's own. */
  const dueFor = useCallback((a: Assignment, studentId: string) =>
    extensions.find(e => e.assignment_id === a.id && e.student_id === studentId)?.due_date ?? a.due_date, [extensions]);
  const reloadExtensions = useCallback(async () => {
    try { setExtensions(await fetchExtensions(assignments.map(a => a.id))); } catch { /* keep what we have */ }
  }, [assignments]);

  const current = useCallback((kind: AssignmentKind) => {
    const t = today();
    // The one due soonest (past-due ones are already gone for students).
    return visible.filter(a => a.kind === kind && isOut(a, t)).sort(byDue)[0];
  }, [visible]);

  const value: Ctx = {
    session, authReady, role, student,
    hydrated, error,
    classrooms, active, setActiveId,
    members, students,
    assignments: visible, drafts, progress, progressFor, passesFor, reloadPasses, current,
    refresh: load, upsertProgress, extensions, dueFor, reloadExtensions,
    cards, setCards, reloadCards,
    billing, canCreate, reloadBilling,
  };

  return <ClassroomContext.Provider value={value}>{children}</ClassroomContext.Provider>;
}

export function useClassroom(): Ctx {
  const c = useContext(ClassroomContext);
  if (!c) throw new Error('useClassroom outside ClassroomProvider');
  return c;
}
